/**
 * CommandBar (+ BulkActionBar) for list pages (DESIGN.md T1): search on the left, view tools,
 * and on the right either the primary actions or — while rows are selected — the bulk actions.
 */
import { SearchIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Input } from '../components/input';
import { useUiStrings } from '../strings';

export interface CommandBarProps {
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Filter, saved views, columns, refresh … */
  tools?: ReactNode;
  /** Shown while nothing is selected. */
  actions?: ReactNode;
  selectedCount?: number;
  /** Replaces `actions` while rows are selected. */
  bulkActions?: ReactNode;
}

export function CommandBar({ search, onSearchChange, searchPlaceholder, tools, actions, selectedCount = 0, bulkActions }: CommandBarProps) {
  const strings = useUiStrings();
  const bulk = selectedCount > 0 && bulkActions;
  return (
    <div role="toolbar" className="flex min-w-0 flex-wrap items-center gap-2">
      {onSearchChange && (
        <div className="relative w-full sm:w-72">
          <SearchIcon aria-hidden className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={search ?? ''}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder ?? strings.search}
            aria-label={searchPlaceholder ?? strings.search}
            className="pl-8"
          />
        </div>
      )}
      {tools}
      <div className="ml-auto flex max-w-full min-w-0 flex-wrap items-center gap-2">
        {bulk ? (
          <>
            <span className="text-sm text-muted-foreground" aria-live="polite">
              {strings.selectedCount.replace('{count}', String(selectedCount))}
            </span>
            {bulkActions}
          </>
        ) : (
          actions
        )}
      </div>
    </div>
  );
}
